/** BE TF ladder (fine → coarse); matches `resolve_candle_type_for_window`. */
export const EXECUTION_CANDLE_TYPE_LADDER = ["m", "5m", "15m", "30m", "h", "4h", "1d"] as const;

export type ExecutionCandleTypeToken = (typeof EXECUTION_CANDLE_TYPE_LADDER)[number];

/** Normalize API / UI candle_type tokens to ladder entries. */
export function normalizeExecutionCandleType(raw: string | undefined | null): string | null {
  if (!raw || typeof raw !== "string") return null;
  const t = raw.trim().toLowerCase();
  if (!t) return null;
  if (t === "1m" || t === "m") return "m";
  if (t === "1h" || t === "h") return "h";
  if (t === "2h") return "h";
  if (EXECUTION_CANDLE_TYPE_LADDER.includes(t as ExecutionCandleTypeToken)) return t;
  return t;
}

export function formatExecutionCandleTypeLabel(raw: string | undefined | null): string {
  const n = normalizeExecutionCandleType(raw);
  if (!n) return "";
  if (n === "m") return "1m";
  if (n === "h") return "1h";
  return n;
}

/**
 * Canonical `candle_type` query values for execution-candles / stats-service.
 * BE `ALLOWED_CANDLE_TYPES` uses `1m`, `1h`, … — bare `m` / `h` fall back to `1m`.
 */
export function toApiExecutionCandleType(raw: string | undefined | null): string | null {
  const n = normalizeExecutionCandleType(raw);
  if (!n) return null;
  if (n === "m") return "1m";
  if (n === "h") return "1h";
  return n;
}

export function executionCandleTypeRank(raw: string | undefined | null): number {
  const n = normalizeExecutionCandleType(raw);
  if (!n) return -1;
  const idx = EXECUTION_CANDLE_TYPE_LADDER.indexOf(n as ExecutionCandleTypeToken);
  return idx >= 0 ? idx : -1;
}

/** True when `a` is strictly finer (smaller bars) than `b`. */
export function isExecutionCandleTypeFinerThan(
  a: string | undefined | null,
  b: string | undefined | null
): boolean {
  const ra = executionCandleTypeRank(a);
  const rb = executionCandleTypeRank(b);
  if (ra < 0 || rb < 0) return false;
  return ra < rb;
}

export function executionCandleTypesMatch(
  a: string | undefined | null,
  b: string | undefined | null
): boolean {
  const na = normalizeExecutionCandleType(a);
  const nb = normalizeExecutionCandleType(b);
  if (!na || !nb) return !na && !nb;
  return na === nb;
}

/** Wall-clock duration of one bar for TF projection (higher TF → span more chart bars). */
export function executionCandleTypeDurationMs(
  raw: string | undefined | null
): number | null {
  const n = normalizeExecutionCandleType(raw);
  if (!n) return null;
  if (n === "m") return 60_000;
  if (n === "5m") return 5 * 60_000;
  if (n === "15m") return 15 * 60_000;
  if (n === "30m") return 30 * 60_000;
  if (n === "h") return 60 * 60_000;
  if (n === "4h") return 4 * 60 * 60_000;
  if (n === "1d") return 24 * 60 * 60_000;
  return null;
}

/**
 * Map a source-TF bar (or span) onto chart-TF wall-clock bounds.
 * Coarser source → wider span on a finer chart (zoom-in); finer source → snap to containing bar.
 * Always expands to at least one full source-TF bar when chart is finer — never keep a
 * single chart-bar from/to that would look like “the 30m candle” sitting on one 5m slot.
 */
export function projectExecutionBarWindowOntoChartTf(opts: {
  barTime: string | null | undefined;
  fromBarTime?: string | null;
  toBarTime?: string | null;
  sourceCandleType: string | null | undefined;
  chartCandleType: string | null | undefined;
}): { fromBarTime: string | null; toBarTime: string | null; barTime: string | null } {
  const barTime = (opts.barTime || "").trim() || null;
  const fromIn = (opts.fromBarTime || "").trim() || null;
  const toIn = (opts.toBarTime || "").trim() || null;
  const anchor = fromIn || barTime || toIn;
  if (!anchor) return { barTime: null, fromBarTime: null, toBarTime: null };

  const srcMs = executionCandleTypeDurationMs(opts.sourceCandleType);
  const chartMs = executionCandleTypeDurationMs(opts.chartCandleType);
  const tAnchor = Date.parse(anchor.replace(/\+00:00$/, "Z"));
  if (!Number.isFinite(tAnchor) || !srcMs || !chartMs) {
    return {
      barTime: barTime || anchor,
      fromBarTime: fromIn || anchor,
      toBarTime: toIn || anchor,
    };
  }

  if (srcMs <= chartMs) {
    return {
      barTime: barTime || anchor,
      fromBarTime: fromIn || anchor,
      toBarTime: toIn || fromIn || anchor,
    };
  }

  const tFromIn = fromIn ? Date.parse(fromIn.replace(/\+00:00$/, "Z")) : Number.NaN;
  const tToIn = toIn ? Date.parse(toIn.replace(/\+00:00$/, "Z")) : Number.NaN;
  const startMs = Number.isFinite(tFromIn) ? tFromIn : tAnchor;
  // At least one full source bar; keep a wider supplied span (e.g. 3-candle HTF FVG).
  const minEndMs = startMs + srcMs - chartMs;
  const endMs =
    Number.isFinite(tToIn) && tToIn >= minEndMs ? tToIn : minEndMs;

  return {
    barTime: barTime || new Date(startMs).toISOString(),
    fromBarTime: new Date(startMs).toISOString(),
    toBarTime: new Date(endMs).toISOString(),
  };
}

/** Options finer than `floor` should be disabled in the candle-type menu. */
export function isExecutionCandleTypeDisabledForBarBudget(
  optionValue: string,
  floor: string | undefined | null
): boolean {
  const floorNorm = normalizeExecutionCandleType(floor);
  if (!floorNorm) return false;
  return isExecutionCandleTypeFinerThan(optionValue, floorNorm);
}

/**
 * Minimum TF to enforce in the candle menu after a coarsened execution-candles response.
 * `resolved_candle_type` echoes the active TF — not the window floor — so do not use it
 * unless BE actually coarsened the user's request.
 */
export function pickExecutionCandlesBarBudgetFloor(
  response: {
    resolved_candle_type?: string | null;
    resolved_window?: { resolved_candle_type?: string | null } | null;
    candle_type?: string | null;
    candle_type_coarsened?: boolean;
  } | null | undefined,
  requestedCandleType: string
): string | null {
  if (!response) return null;
  if (response.candle_type_coarsened === true) {
    return pickResolvedExecutionCandleType(response);
  }
  if (response.candle_type_coarsened === false) return null;
  const resolved = pickResolvedExecutionCandleType(response);
  const requested = pickRequestedExecutionCandleType(response, requestedCandleType);
  if (!resolved || !requested) return null;
  if (!isExecutionCandleTypeFinerThan(requested, resolved)) return null;
  return resolved;
}

export function pickResolvedExecutionCandleType(response: {
  resolved_candle_type?: string | null;
  resolved_window?: { resolved_candle_type?: string | null } | null;
  candle_type?: string | null;
} | null | undefined): string | null {
  if (!response) return null;
  return (
    normalizeExecutionCandleType(response.resolved_candle_type) ??
    normalizeExecutionCandleType(response.resolved_window?.resolved_candle_type) ??
    normalizeExecutionCandleType(response.candle_type)
  );
}

export function pickRequestedExecutionCandleType(
  response: { candle_type?: string | null } | null | undefined,
  fallbackUserSelection: string
): string {
  return normalizeExecutionCandleType(response?.candle_type) ?? normalizeExecutionCandleType(fallbackUserSelection) ?? fallbackUserSelection;
}
